// src/components/_v3/touristic-site/AnchorSubNav.jsx
import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';

const SubNavWrapStyled = styled.div`
  position: sticky;
  top: 64px;
  z-index: 50;
  background: ${props => props.theme.colors.paper};
  border-bottom: 1px solid ${props => props.theme.colors.line};
`;

const SubNavStyled = styled.div`
  display: flex;
  gap: .3rem;
  overflow-x: auto;
  padding-block: .5rem;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }

  a {
    flex: none;
    text-decoration: none;
    color: ${props => props.theme.colors.inkSoft};
    font-weight: 700;
    font-size: .85rem;
    padding: .45rem .9rem;
    border-radius: 8px;
  }

  a.active {
    color: ${props => props.theme.colors.brandText};
    background: ${props => props.theme.colors.paperCard};
    box-shadow: ${props => props.theme.shadow};
  }
`;

const AnchorSubNav = ({sections}) => {
  const {t} = useTranslation(['touristic-site']);
  const [activeId, setActiveId] = useState(sections.length > 0 ? sections[0].id : null);

  // Derive a stable key from section ids to avoid observer churn when the
  // parent passes a new array reference on every render but the content is
  // unchanged (buildSections is not memoized at the call site).
  const sectionIds = sections.map(s => s.id).join(',');

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, {rootMargin: '-45% 0px -50% 0px'});

    sections.forEach(({id}) => {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el);
      }
    });
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIds]);

  const handleClick = (event, id) => {
    event.preventDefault();
    const target = document.getElementById(id);
    if (target) {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({behavior: reduced ? 'auto' : 'smooth', block: 'start'});
    }
  };

  return (
    <SubNavWrapStyled>
      <SubNavStyled>
        {sections.map(({id, i18nKey}) => (
          <a
            key={id}
            href={`#${id}`}
            className={activeId === id ? 'active' : undefined}
            onClick={(event) => handleClick(event, id)}
          >
            {t(i18nKey)}
          </a>
        ))}
      </SubNavStyled>
    </SubNavWrapStyled>
  );
};

export default AnchorSubNav;
